/**
 * DriftManagementTab Component
 *
 * Core drift management interface extracted from the ConfigurationDrift page.
 * Handles drift detection, approval workflows, batch processing, and audit
 * export. Includes stats strip, Active/Archive sub-tabs, filters, bulk actions,
 * drift details sidebar, and all associated modals.
 *
 * Part of the Baseline Compliance hub.
 */

import { useState, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import clsx from 'clsx'
import { api } from '@/lib/api'
import type { ConfigurationDrift } from '@/types'
import DriftTableWithBulkActions from '@/components/Drift/DriftTableWithBulkActions'
import DriftDetailDrawer from '@/components/Drift/DriftDetailDrawer'
import BaselineRefreshDialog from '@/components/BaselineRefreshDialog'
import RejectionNotificationModal from '@/components/RejectionNotificationModal'
import DriftAuditExportModal from '@/components/Drift/DriftAuditExportModal'
import toast from 'react-hot-toast'
import { useTenantContext } from '@/hooks/useTenantContext'
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// Honest in-flight state: we know the total going in, but we don't know
// sub-step progress because processBatchedApprovals is a single Promise
// that resolves at the end. No fake percentage, no fake stages.
type ProcessingState = { isProcessing: boolean; totalToProcess: number }
const IDLE_PROCESSING_STATE: ProcessingState = { isProcessing: false, totalToProcess: 0 }

export interface DriftManagementTabProps {
  /** The tenant ID to display drift data for (parent handles tenant selection) */
  tenantId: string | undefined
  /**
   * Optional controlled-mode selection for the drift detail drawer. When
   * provided, the parent owns the drawer's open state — useful when other
   * surfaces on the page (e.g. the Section · 02 "Top items" rows) need to
   * open the same drawer without rendering a parallel instance. When omitted
   * the component falls back to its own internal state so existing callers
   * keep working.
   */
  selectedDriftId?: string | null
  onSelectDrift?: (id: string | null) => void
}

export default function DriftManagementTab({
  tenantId,
  selectedDriftId: controlledSelectedDriftId,
  onSelectDrift,
}: DriftManagementTabProps) {
  const { userTenants, isGlobalAdmin } = useTenantContext()
  const [filters, setFilters] = useState<{
    tenantId: string
    severity: string
    status: string
    fromDate?: string
    toDate?: string
  }>({
    tenantId: '',
    severity: '',
    status: '',
    fromDate: '',
    toDate: '',
  })
  const [hideRemediated, setHideRemediated] = useState(true)
  const [hideAwaitingRemediation, setHideAwaitingRemediation] = useState(false)
  const [viewMode, setViewMode] = useState<'active' | 'archive'>('active')
  const [currentPage, setCurrentPage] = useState(1)
  const [internalSelectedDriftId, setInternalSelectedDriftId] = useState<string | null>(null)
  // Controlled vs uncontrolled selection: prefer the parent's state when it's
  // managing the drawer, otherwise fall back to local state.
  const isSelectionControlled = controlledSelectedDriftId !== undefined
  const selectedDriftId = isSelectionControlled
    ? controlledSelectedDriftId ?? null
    : internalSelectedDriftId
  const setSelectedDriftId = (id: string | null) => {
    if (isSelectionControlled) {
      onSelectDrift?.(id)
    } else {
      setInternalSelectedDriftId(id)
    }
  }
  const [showBaselineRefreshDialog, setShowBaselineRefreshDialog] = useState(false)
  const [showRejectionNotification, setShowRejectionNotification] = useState(false)
  const [rejectedDriftCount, setRejectedDriftCount] = useState(0)
  const [showAuditExportModal, setShowAuditExportModal] = useState(false)
  const [activeScanJobs, setActiveScanJobs] = useState<Map<string, string>>(new Map())
  const pageSize = 20

  const queryClient = useQueryClient()

  // Sync filters.tenantId from the tenantId prop
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      tenantId: tenantId || '',
    }))
    setCurrentPage(1)
  }, [tenantId])

  const {
    data: driftData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['configuration-drift', filters, currentPage, hideRemediated, hideAwaitingRemediation, viewMode],
    queryFn: () => {
      let statusFilter = filters.status

      if (viewMode === 'archive') {
        statusFilter = 'Remediated'
      } else {
        const allStatuses = ['New', 'Pending', 'PendingApproval', 'Approved', 'Rejected', 'Ignored', 'AwaitingRemediation', 'Remediated']
        const excludedStatuses: string[] = []

        if (hideRemediated) excludedStatuses.push('Remediated')
        if (hideAwaitingRemediation) excludedStatuses.push('AwaitingRemediation')

        if (!statusFilter) {
          const includedStatuses = allStatuses.filter(s => !excludedStatuses.includes(s))
          statusFilter = includedStatuses.join(',')
        } else {
          const currentStatusList = statusFilter.split(',')
          const filteredStatusList = currentStatusList.filter(s => !excludedStatuses.includes(s))
          statusFilter = filteredStatusList.length > 0 ? filteredStatusList.join(',') : 'New'
        }
      }

      const effectiveTenantId = !isGlobalAdmin && userTenants.length === 1
        ? userTenants[0].tenantId
        : filters.tenantId

      if (isGlobalAdmin && (!effectiveTenantId || effectiveTenantId === '')) {
        // Match api.getConfigurationDrifts' return shape so the useQuery
        // overload picks up the right TQueryFnData and downstream `driftData`
        // accessors (.items / .total) keep typing.
        return Promise.resolve({
          items: [] as ConfigurationDrift[],
          total: 0,
          page: currentPage,
          limit: pageSize,
        })
      }

      return api.getConfigurationDrifts({
        tenantId: effectiveTenantId || undefined,
        severity: filters.severity || undefined,
        status: statusFilter || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        page: currentPage,
        limit: pageSize,
      })
    },
    placeholderData: keepPreviousData,
    enabled: !!tenantId,
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: string; comment?: string }) =>
      api.updateDriftStatus(id, status, comment),
    onSuccess: (updatedDrift, { id }) => {
      queryClient.setQueryData(['configuration-drift', id], updatedDrift)

      queryClient.setQueryData(['configuration-drift', filters, currentPage], (oldData: any) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          items: oldData.items.map((item: any) =>
            item.id === id ? updatedDrift : item
          )
        }
      })

      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
      toast.success('Status updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update status')
    },
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.approveDrift(id, comment),
    onSuccess: (response, { id }) => {
      const updatedDrift = response.data
      const autoApprovedCount = response.autoApprovedCount || 0
      const message = response.message || "Drift approved successfully!"

      queryClient.setQueryData(['configuration-drift', id], updatedDrift)

      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.refetchQueries({ queryKey: ['configuration-drift'] })

      toast.success(
        <div>
          <div className="font-medium">{message}</div>
          <div className="mt-1 text-sm">
            {autoApprovedCount > 0
              ? `Changes have been queued for processing. Click "Process Queued Changes" to create pull requests.`
              : `Change has been queued for processing. Click "Process Queued Changes" to create pull requests.`
            }
          </div>
        </div>,
        { duration: autoApprovedCount > 0 ? 8000 : 6000 }
      )
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to approve drift')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      api.rejectDrift(id, comment),
    onSuccess: (updatedDrift, { id }) => {
      queryClient.setQueryData(['configuration-drift', id], updatedDrift)

      queryClient.setQueryData(['configuration-drift', filters, currentPage], (oldData: any) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          items: oldData.items.map((item: any) =>
            item.id === id ? updatedDrift : item
          )
        }
      })

      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
      toast.success('Drift rejected successfully')

      setRejectedDriftCount(1)
      setShowRejectionNotification(true)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject drift')
    },
  })

  // Query for pending batched approvals count
  const { data: queuedApprovals } = useQuery({
    queryKey: ['queued-approvals', filters.tenantId],
    queryFn: () => {
      return api.getConfigurationDrifts({
        tenantId: filters.tenantId,
        status: 'Approved',
        unprocessedOnly: true,
        limit: 1000
      })
    },
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 30000,
    select: (data) => ({
      count: data?.items?.length || 0,
      oldestApproval: data?.items?.reduce((oldest: any, current: any) => {
        if (!oldest) return current
        return new Date(current.approvedAt) < new Date(oldest.approvedAt) ? current : oldest
      }, null)
    })
  })

  // Query for recent scan jobs to show last drift check time
  const { data: recentScanJobs } = useQuery({
    queryKey: ['recent-scan-jobs', filters.tenantId],
    queryFn: () => api.getRecentScanJobs(filters.tenantId, 5),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 60000,
  })

  // Cheap "Archive" count for the view-toggle badge. We only ever need
  // `total` here, so request a single-row page — payload size stays small
  // while the response still carries the unfiltered Remediated total.
  const { data: archiveCountData } = useQuery({
    queryKey: ['configuration-drift-archive-count', filters.tenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId: filters.tenantId,
        status: 'Remediated',
        page: 1,
        limit: 1,
      }),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    staleTime: 5 * 60 * 1000,
  })
  const archiveCount = archiveCountData?.total ?? 0

  // Dashboard stats — feed the severity chip rail in the table head (Slice 2).
  // The legacy stats strip is gone (numbers already live in the Guardian Score
  // composite at the page top), so we only pass `issuesBySeverity` through to
  // the table.
  const { data: statsData } = useQuery({
    queryKey: ['dashboard', filters.tenantId],
    queryFn: () => api.getDashboardStats(filters.tenantId),
    enabled: !!filters.tenantId && filters.tenantId !== '',
    refetchInterval: 5 * 60 * 1000,
  })

  // Find the last completed ConfigurationScan job
  const lastCompletedScan = recentScanJobs?.find(
    (job: any) => job.jobType === 'ConfigurationScan' &&
      (job.status === 'Completed' || job.status === 'CompletedWithDrift')
  )

  const [processingState, setProcessingState] = useState<ProcessingState>(IDLE_PROCESSING_STATE)

  const processBatchMutation = useMutation({
    mutationFn: async (tid: string) => {
      const totalToProcess = queuedApprovals?.count || 0
      setProcessingState({
        isProcessing: true,
        totalToProcess
      })

      toast.loading(
        <div>
          <div className="font-medium">
            Working on {totalToProcess} approved change{totalToProcess === 1 ? '' : 's'}
          </div>
          <div className="mt-1 text-sm">This may take a few minutes.</div>
        </div>,
        { id: 'batch-processing', duration: Infinity }
      )

      try {
        const result = await api.processBatchedApprovals(tid)
        return result
      } catch (error) {
        setProcessingState(IDLE_PROCESSING_STATE)
        throw error
      }
    },
    onSuccess: (result) => {
      toast.dismiss('batch-processing')

      setProcessingState(IDLE_PROCESSING_STATE)

      try {
        queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
        queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
      } catch (error) {
        console.error('Error refreshing queries after batch processing:', error)
      }

      if (result?.pullRequests && Array.isArray(result.pullRequests) && result.pullRequests.length > 0) {
        toast.success(
          <div>
            <div className="font-medium">Successfully Processed {result.processed} Changes!</div>
            <div className="mt-2 text-sm">
              <div className="font-medium mb-1">Created {result.pullRequests.length} pull request{result.pullRequests.length === 1 ? '' : 's'}:</div>
              <div className="space-y-1">
                {result.pullRequests.map(pr => (
                  <div key={pr.prId} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded">
                    <span className="text-xs">{pr.driftCount} changes for {pr.tenantId.substring(0, 8)}...</span>
                    {isGlobalAdmin ? (
                      <a
                        href={pr.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View PR #{pr.prId}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-600 font-medium">
                        PR #{pr.prId} (pending)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              {isGlobalAdmin
                ? "Changes will be reflected in the baseline after PR approval and merge."
                : "The iuvo team will review and merge these baseline updates. Changes will be reflected in subsequent scans."
              }
            </div>
          </div>,
          { duration: 12000 }
        )
      } else if (result?.processed > 0) {
        toast.success(
          <div>
            <div className="font-medium">Successfully Processed {result.processed} Changes!</div>
            <div className="mt-1 text-sm">Baseline updates have been applied directly (no PRs created).</div>
          </div>,
          { duration: 6000 }
        )
      } else {
        toast('No changes were processed. All approved drifts may have already been handled.', { duration: 4000 })
      }
    },
    onError: (error: any) => {
      toast.dismiss('batch-processing')

      setProcessingState(IDLE_PROCESSING_STATE)

      console.error('Batch processing error:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred'
      toast.error(
        <div>
          <div className="font-medium">Failed to Process Queued Approvals</div>
          <div className="mt-1 text-sm">{errorMessage}</div>
          <div className="mt-2 text-xs text-gray-600">
            Your approvals are still saved. You can try processing them again.
          </div>
        </div>,
        { duration: 8000 }
      )
    },
  })

  const scanMutation = useMutation({
    mutationFn: (tid: string) => api.scanTenant(tid),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
      queryClient.invalidateQueries({ queryKey: ['scanLimits'] })

      if (data && data.status === 'Failed') {
        toast.error(data.errorMessage || 'Scan failed')
      } else if (data && data.status === 'RateLimited') {
        toast.error(data.errorMessage || 'Daily scan limit exceeded')
      } else if (data && data.status === 'Queued' && data.jobId) {
        setActiveScanJobs(prev => new Map(prev.set(data.tenantId, data.jobId)))
        toast.success('Scan job created successfully. Monitoring progress...')
        startJobPolling(data.jobId, data.tenantId)
      } else {
        toast.success('Scan initiated successfully')
      }
    },
    onError: (error: any) => {
      console.error('Scan mutation error:', error)
      if (error.response?.status === 429) {
        toast.error('Daily scan limit exceeded')
      } else if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'Another scan or baseline refresh is already in progress for this tenant. Please wait for it to complete.'
        toast.error(message, { duration: 6000 })
      } else if (error.response?.status >= 400) {
        toast.error(error.response?.data?.message || error.response?.data?.errorMessage || 'Failed to initiate scan')
      }
    },
  })

  const startJobPolling = (jobId: string, jobTenantId: string) => {
    const pollJob = async () => {
      try {
        const jobStatus = await api.getScanJobStatus(jobId)

        if (jobStatus.status === 'Completed') {
          setActiveScanJobs(prev => {
            const newMap = new Map(prev)
            newMap.delete(jobTenantId)
            return newMap
          })
          toast.success('Scan completed successfully!')
          queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        } else if (jobStatus.status === 'Failed') {
          setActiveScanJobs(prev => {
            const newMap = new Map(prev)
            newMap.delete(jobTenantId)
            return newMap
          })
          toast.error(`Scan failed: ${jobStatus.errorMessage || 'Unknown error'}`)
        } else if (jobStatus.status === 'Running' || jobStatus.status === 'Queued') {
          setTimeout(pollJob, 3000)
        }
      } catch (error) {
        console.error('Error polling job status:', error)
        setActiveScanJobs(prev => {
          const newMap = new Map(prev)
          newMap.delete(jobTenantId)
          return newMap
        })
      }
    }

    setTimeout(pollJob, 2000)
  }

  const handleScanNow = () => {
    if (filters.tenantId && filters.tenantId !== '') {
      scanMutation.mutate(filters.tenantId)
    }
  }

  const handleStatusUpdate = (id: string, status: string, comment?: string) => {
    updateStatusMutation.mutate({ id, status, comment })
  }

  const handleApprove = (id: string, comment?: string) => {
    approveMutation.mutate({ id, comment })
  }

  const handleReject = (id: string, comment?: string) => {
    rejectMutation.mutate({ id, comment })
  }

  const handleProcessQueuedChanges = () => {
    if (!filters.tenantId) return
    processBatchMutation.mutate(filters.tenantId)
  }

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters({
      ...newFilters,
      tenantId: tenantId || '',
    })
    setCurrentPage(1)
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Failed to load configuration drift data</div>
        <button onClick={() => refetch()} className="btn-primary">
          Retry
        </button>
      </div>
    )
  }

  // Derived state shared by Band A and the table props
  const hasTenant = !!filters.tenantId && filters.tenantId !== ''
  const hasLocalActiveScan = hasTenant && !!activeScanJobs.get(filters.tenantId)
  const activeJobFromServer = recentScanJobs?.find(
    (job: any) =>
      (job.status === 'Queued' || job.status === 'Running') &&
      (job.jobType === 'ConfigurationScan' || job.jobType === 'BaselineRefresh'),
  )
  const isScanning = hasLocalActiveScan || !!activeJobFromServer
  const queuedCount = queuedApprovals?.count ?? 0
  const oldestApprovalDate = queuedApprovals?.oldestApproval?.approvedAt
    ? new Date(queuedApprovals.oldestApproval.approvedAt)
    : null
  const activeBadge = viewMode === 'active' ? driftData?.total ?? 0 : undefined
  // In archive mode `driftData.total` is the archive total — fall back to the
  // independent archive-count query when we're in active mode so the badge
  // still reads correctly without a second roundtrip per view-flip.
  const archiveBadge = viewMode === 'archive' ? driftData?.total ?? 0 : archiveCount

  return (
    <div>
      {/* ============================================================
          BAND A · Action rail
          Folds in: §1 last-scan pill, §2 action buttons, §4 view-mode
          toggle. Single horizontal row sitting under the section
          headline rendered by ConfigurationDrift.tsx.
          ============================================================ */}
      <ActionRail>
        <div className="flex flex-wrap items-center gap-3.5">
          <ViewToggle
            value={viewMode}
            activeCount={activeBadge}
            archiveCount={archiveBadge}
            onChange={setViewMode}
            disabled={!hasTenant}
          />
          <ScanPill
            scanning={isScanning}
            lastCompletedAt={
              lastCompletedScan?.completedAt || lastCompletedScan?.createdAt || null
            }
          />
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <OutlineButton
            onClick={handleScanNow}
            disabled={!hasTenant || scanMutation.isPending || hasLocalActiveScan}
            icon={
              scanMutation.isPending || hasLocalActiveScan ? (
                <ArrowPathIcon className="h-3 w-3 animate-spin" />
              ) : (
                <MagnifyingGlassIcon className="h-3 w-3" />
              )
            }
          >
            {scanMutation.isPending
              ? 'Creating…'
              : hasLocalActiveScan
              ? 'Scanning…'
              : 'Scan now'}
          </OutlineButton>
          <OutlineButton
            onClick={() => setShowBaselineRefreshDialog(true)}
            disabled={!hasTenant}
            icon={<ArrowPathIcon className="h-3 w-3" />}
          >
            Refresh baseline
          </OutlineButton>
          <OutlineButton
            onClick={() => setShowAuditExportModal(true)}
            disabled={!hasTenant}
            icon={<ArrowDownTrayIcon className="h-3 w-3" />}
          >
            Export audit
          </OutlineButton>
          {queuedCount > 0 && (
            <PrimaryButton
              onClick={handleProcessQueuedChanges}
              disabled={processBatchMutation.isPending}
              count={queuedCount}
            >
              {processingState.isProcessing
                ? `Working on ${processingState.totalToProcess}`
                : 'Commit changes'}
            </PrimaryButton>
          )}
        </div>
      </ActionRail>

      {/* ============================================================
          BAND B · Notice rail
          Folds in: §5 queued-approvals banner (default + tone-info
          while processing) and §8 archive-view banner.
          ============================================================ */}
      {viewMode === 'archive' ? (
        <NoticeRail
          tone="archive"
          eyebrow="Archive view"
          headline={
            <>
              Closed for the <em>record.</em>
            </>
          }
          body={
            <>
              Showing{' '}
              <strong className="font-semibold text-[color:var(--color-navy,#212938)]">
                {archiveBadge ?? 0} remediated drift
                {archiveBadge === 1 ? '' : 's'}
              </strong>{' '}
              kept for audit. Bulk actions are disabled here. Switch back to{' '}
              <strong className="font-semibold text-[color:var(--color-navy,#212938)]">
                Active
              </strong>{' '}
              to make changes.
            </>
          }
          actions={
            <OutlineButton onClick={() => setViewMode('active')}>
              Back to active
            </OutlineButton>
          }
        />
      ) : processingState.isProcessing ? (
        <NoticeRail
          tone="info"
          eyebrow="In flight"
          headline={
            <>
              {processingState.totalToProcess} change
              {processingState.totalToProcess === 1 ? ' is' : 's are'}{' '}
              <em>landing now.</em>
            </>
          }
          body={
            <>
              We&apos;ll surface the pull requests as soon as the batch returns.
              This usually takes a few minutes.
            </>
          }
        />
      ) : queuedCount > 0 ? (
        <NoticeRail
          tone="default"
          eyebrow="Heads up"
          headline={
            <>
              {queuedCount} change{queuedCount === 1 ? ' is' : 's are'}{' '}
              <em>queuing up.</em>
            </>
          }
          body={
            <>
              You&apos;ve approved{' '}
              <strong className="font-semibold text-[color:var(--color-navy,#212938)]">
                {queuedCount} drift{queuedCount === 1 ? '' : 's'}
              </strong>{' '}
              waiting on the next batch run.
              {oldestApprovalDate && (
                <>
                  {' '}
                  The oldest has been queued since{' '}
                  <strong className="font-semibold text-[color:var(--color-navy,#212938)]">
                    {oldestApprovalDate.toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </strong>
                  .
                </>
              )}{' '}
              Hit{' '}
              <strong className="font-semibold text-[color:var(--color-navy,#212938)]">
                Commit changes
              </strong>{' '}
              to open the pull requests.
            </>
          }
          actions={
            <PrimaryButton
              onClick={handleProcessQueuedChanges}
              disabled={processBatchMutation.isPending}
              count={queuedCount}
            >
              Commit now
            </PrimaryButton>
          }
        />
      ) : null}

      {/* ============================================================
          View filters — replaces the legacy two-checkbox card.
          Hidden in archive mode (the archive notice signals read-only).
          ============================================================ */}
      {viewMode === 'active' && (
        <div className="mt-[18px] flex flex-wrap items-center gap-2.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
            Hiding
          </span>
          <FilterChip
            active={hideRemediated}
            onClick={() => setHideRemediated(!hideRemediated)}
          >
            Remediated
          </FilterChip>
          <FilterChip
            active={hideAwaitingRemediation}
            onClick={() => setHideAwaitingRemediation(!hideAwaitingRemediation)}
          >
            Awaiting remediation
          </FilterChip>
        </div>
      )}

      {/* ============================================================
          BAND C · Queue table — the chip-rail head, bulk-action band,
          grouped rows, and Refine popover all live inside the table
          component now (the legacy <DriftFilters> card was retired).
          ============================================================ */}
      <div className="mt-7">
        <DriftTableWithBulkActions
          drifts={driftData?.items || []}
          isLoading={isLoading}
          onRowClick={setSelectedDriftId}
          selectedId={selectedDriftId}
          onRefresh={() => {
            queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
            queryClient.invalidateQueries({ queryKey: ['queued-approvals'] })
            queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
            queryClient.refetchQueries({ queryKey: ['queued-approvals'] })
          }}
          pagination={{
            current: currentPage,
            total: driftData?.total || 0,
            pageSize,
            onPageChange: setCurrentPage,
          }}
          filters={filters}
          onFilterChange={handleFilterChange}
          severityCounts={
            statsData?.issuesBySeverity
              ? {
                  critical: statsData.issuesBySeverity.critical ?? 0,
                  high: statsData.issuesBySeverity.high ?? 0,
                  medium: statsData.issuesBySeverity.medium ?? 0,
                  low: statsData.issuesBySeverity.low ?? 0,
                }
              : undefined
          }
          archiveMode={viewMode === 'archive'}
        />
      </div>

      {/* Drift Details Drawer */}
      <DriftDetailDrawer
        driftId={selectedDriftId}
        isOpen={!!selectedDriftId}
        onClose={() => setSelectedDriftId(null)}
        onStatusUpdate={handleStatusUpdate}
        onApprove={handleApprove}
        onReject={handleReject}
        isUpdating={
          updateStatusMutation.isPending ||
          approveMutation.isPending ||
          rejectMutation.isPending
        }
      />

      {/* Baseline Refresh Dialog */}
      <BaselineRefreshDialog
        isOpen={showBaselineRefreshDialog}
        onClose={() => setShowBaselineRefreshDialog(false)}
        tenantId={filters.tenantId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['configuration-drift'] })
          queryClient.refetchQueries({ queryKey: ['configuration-drift'] })
        }}
      />

      {/* Rejection Notification Modal */}
      <RejectionNotificationModal
        isOpen={showRejectionNotification}
        onClose={() => setShowRejectionNotification(false)}
        rejectedCount={rejectedDriftCount}
        isBulkAction={rejectedDriftCount > 1}
      />

      {/* Drift Audit Export Modal */}
      <DriftAuditExportModal
        isOpen={showAuditExportModal}
        onClose={() => setShowAuditExportModal(false)}
        tenantId={filters.tenantId}
        tenantName={userTenants.find(t => t.tenantId === filters.tenantId)?.name}
      />
    </div>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
   Editorial primitives — kept private to this file per the slice scope.
   If/when these get reused from another posture page, promote them into
   src/components/PostureBrief/ alongside SectionHeadline.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * ActionRail — Band A wrapper. A single horizontal row with a top/bottom rule,
 * left + right child slots. Wraps to two rows on narrow viewports.
 */
function ActionRail({ children }: { children: ReactNode }) {
  return (
    <div className="mt-[26px] flex flex-wrap items-center justify-between gap-[18px] border-y border-[color:var(--color-rule,#E6E1D5)] py-[18px]">
      {children}
    </div>
  )
}

/**
 * ViewToggle — segmented pill that flips between Active and Archive. Shows
 * mono count badges so the user can tell at a glance whether the archive is
 * worth visiting.
 */
function ViewToggle({
  value,
  activeCount,
  archiveCount,
  onChange,
  disabled,
}: {
  value: 'active' | 'archive'
  activeCount?: number
  archiveCount?: number
  onChange: (next: 'active' | 'archive') => void
  disabled?: boolean
}) {
  return (
    <div
      className={clsx(
        'inline-flex items-center rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper-warm,#FBF8F2)] p-[3px]',
        disabled && 'opacity-50 pointer-events-none',
      )}
      role="tablist"
      aria-label="Drift view mode"
    >
      <ViewToggleButton
        active={value === 'active'}
        count={activeCount}
        onClick={() => onChange('active')}
      >
        Active
      </ViewToggleButton>
      <ViewToggleButton
        active={value === 'archive'}
        count={archiveCount}
        onClick={() => onChange('archive')}
      >
        Archive
      </ViewToggleButton>
    </div>
  )
}

function ViewToggleButton({
  active,
  count,
  onClick,
  children,
}: {
  active: boolean
  count?: number
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-2 rounded-full px-4 py-[7px] text-[10px] font-bold uppercase tracking-[0.16em] transition-colors',
        active
          ? 'bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
          : 'text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)]',
      )}
    >
      {children}
      {typeof count === 'number' && (
        <span
          className={clsx(
            'rounded-full px-[6px] py-[2px] font-mono text-[9px] font-bold tabular-nums tracking-[0.04em]',
            active
              ? 'bg-[color:var(--color-paper,#fff)]/20 text-[color:var(--color-paper,#fff)]'
              : 'bg-[color:var(--color-rule,#E6E1D5)] text-[color:var(--color-navy,#212938)]',
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

/**
 * ScanPill — thin paper-card pill summarizing scan state. Hidden when there's
 * no completed scan and we're not currently scanning (keeps the rail quiet
 * for first-run tenants).
 */
function ScanPill({
  scanning,
  lastCompletedAt,
}: {
  scanning: boolean
  lastCompletedAt: string | null
}) {
  if (!scanning && !lastCompletedAt) return null

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-2 rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-3.5 py-[7px] text-[11px] font-semibold text-[color:var(--color-mute,#6B7280)]',
        'shadow-[0_1px_2px_rgba(33,41,56,0.04),0_8px_24px_rgba(33,41,56,0.04)]',
      )}
      title={
        scanning
          ? 'Configuration scan in progress'
          : lastCompletedAt
          ? `Last completed scan ${new Date(lastCompletedAt).toLocaleString()}`
          : undefined
      }
    >
      <span
        className={clsx(
          'h-1.5 w-1.5 rounded-full',
          scanning
            ? 'animate-pulse bg-[color:var(--color-blue-deep,#7BA8C4)] shadow-[0_0_6px_rgba(123,168,196,0.5)]'
            : 'bg-[color:var(--color-accent-deep,#4A9B6E)] shadow-[0_0_6px_rgba(118,214,157,0.5)]',
        )}
      />
      {scanning ? (
        <>
          <strong className="font-bold text-[color:var(--color-navy,#212938)]">
            Scan in progress.
          </strong>
        </>
      ) : (
        <>
          Last scan{' '}
          <strong className="font-bold text-[color:var(--color-navy,#212938)]">
            {formatRelativeTime(lastCompletedAt as string)}
          </strong>
        </>
      )}
    </span>
  )
}

/**
 * OutlineButton — small navy-outline pill, optional leading icon. Used for
 * the right-side "Scan now / Refresh baseline / Export audit" cluster on
 * Band A and for the secondary CTA in archive notices.
 */
export function OutlineButton({
  children,
  onClick,
  disabled,
  icon,
  title,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  icon?: ReactNode
  title?: string
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'inline-flex items-center gap-2 rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-navy,#212938)] transition-colors',
        'hover:border-[color:var(--color-navy,#212938)]',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[color:var(--color-rule,#E6E1D5)]',
      )}
    >
      {icon}
      {children}
    </button>
  )
}

/**
 * PrimaryButton — sand-deep filled CTA used for the "Commit changes / Commit
 * now" promotion. Shows a mono count badge when supplied.
 */
export function PrimaryButton({
  children,
  onClick,
  disabled,
  count,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  count?: number
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex items-center gap-2.5 rounded-full border border-[color:var(--color-sand-deep,#C9AD80)] bg-[color:var(--color-sand-deep,#C9AD80)] px-[18px] py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-transform',
        'shadow-[0_4px_14px_rgba(201,173,128,0.35)] hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(201,173,128,0.5)]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_14px_rgba(201,173,128,0.35)]',
      )}
    >
      {children}
      {typeof count === 'number' && (
        <span className="rounded-full bg-white/20 px-2 py-[1px] font-mono text-[10px] font-bold tabular-nums">
          {count}
        </span>
      )}
    </button>
  )
}

/**
 * NoticeRail — Band B. Editorial notice with a sand-deep / blue-deep / mute
 * left rule depending on tone. Eyebrow + italic-mixed headline + body + an
 * optional actions slot on the right.
 */
export function NoticeRail({
  tone = 'default',
  eyebrow,
  headline,
  body,
  actions,
  className,
}: {
  tone?: 'default' | 'info' | 'archive'
  eyebrow: string
  headline: ReactNode
  body: ReactNode
  actions?: ReactNode
  className?: string
}) {
  const leftRuleClass =
    tone === 'info'
      ? 'border-l-[color:var(--color-blue-deep,#7BA8C4)]'
      : tone === 'archive'
      ? 'border-l-[color:var(--color-mute,#6B7280)]'
      : 'border-l-[color:var(--color-sand-deep,#C9AD80)]'
  const emColorClass =
    tone === 'info'
      ? '[&_em]:[color:var(--color-blue-deep,#7BA8C4)]'
      : tone === 'archive'
      ? '[&_em]:[color:var(--color-mute,#6B7280)]'
      : '[&_em]:[color:var(--color-sand-deep,#C9AD80)]'

  return (
    <div
      className={clsx(
        'mt-7 grid grid-cols-[1fr_auto] items-center gap-7 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] border-l-[4px] bg-[color:var(--color-paper-warm,#FBF8F2)] px-7 py-[22px]',
        leftRuleClass,
        className,
      )}
      role="status"
    >
      <div>
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
          {eyebrow}
        </div>
        <h4
          className={clsx(
            'font-display text-[18px] font-extrabold leading-[1.2] tracking-[-0.012em] text-[color:var(--color-navy,#212938)]',
            '[&_em]:font-semibold [&_em]:italic',
            emColorClass,
          )}
        >
          {headline}
        </h4>
        <p className="mt-1 max-w-[560px] text-[12px] leading-[1.55] text-[color:var(--color-mute,#6B7280)]">
          {body}
        </p>
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2.5 justify-self-end">
          {actions}
        </div>
      )}
    </div>
  )
}

/**
 * FilterChip — inline toggle for the "Hiding" rail. Active chips render with
 * a navy fill and a soft × glyph; dormant chips render as quiet paper pills.
 */
function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors',
        active
          ? 'border-[color:var(--color-navy,#212938)] bg-[color:var(--color-navy,#212938)] text-[color:var(--color-paper,#fff)]'
          : 'border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] text-[color:var(--color-navy,#212938)] hover:border-[color:var(--color-navy,#212938)]',
      )}
    >
      {children}
      <span
        className={clsx(
          'text-[12px] leading-none',
          active ? 'text-[color:var(--color-paper,#fff)]/60' : 'text-[color:var(--color-faint,#9CA3AF)]',
        )}
        aria-hidden
      >
        {active ? '×' : '+'}
      </span>
    </button>
  )
}
